import React, { useEffect, useState } from "react";
import { UsersThree, EnvelopeSimple, Spinner, ArrowClockwise, CheckCircle, Clock, XCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const STATE_STYLES = {
  pending: { icon: Clock, color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  used: { icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30" },
  expired: { icon: XCircle, color: "text-zinc-500", bg: "bg-zinc-800 border-zinc-700" },
};

export default function InviteTeammate() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");
  const [resending, setResending] = useState(null);

  const resend = async (inviteId) => {
    setResending(inviteId);
    try {
      const { data } = await api.post(`/admin/invites/${inviteId}/resend`);
      toast.success(`Invitation resent to ${data.invited_email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setResending(null);
    }
  };

  const load = async () => {
    setLoadingList(true);
    setListError("");
    try {
      const { data } = await api.get("/admin/invites");
      setInvites(data.invites || []);
    } catch (err) {
      setListError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load invitations");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/invites", { email: email.trim(), name: name.trim() });
      toast.success(`Invitation sent to ${email}`);
      setEmail("");
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 lg:col-span-2" data-testid="invite-teammate-card">
      <div className="flex items-center gap-2 mb-3">
        <UsersThree size={18} weight="duotone" className="text-orange-400" />
        <div className="font-display font-medium text-zinc-100">Invite Teammate</div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Send a magic-link signup to a VA or junior closer. They'll set their own password and get a fully-isolated pipeline
        (their leads stay private from yours).
      </p>

      <form onSubmit={onSubmit} className="grid md:grid-cols-[1fr_1fr_auto] gap-3 mb-5">
        <div>
          <Label htmlFor="invite-name" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Full name</Label>
          <Input
            id="invite-name"
            data-testid="invite-teammate-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Jamie Doe"
            required
            className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:border-orange-500"
          />
        </div>
        <div>
          <Label htmlFor="invite-email" className="text-xs font-mono uppercase tracking-wider text-zinc-500">Email</Label>
          <Input
            id="invite-email"
            data-testid="invite-teammate-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jamie@example.com"
            required
            className="mt-1.5 bg-zinc-950 border-zinc-800 focus-visible:border-orange-500"
          />
        </div>
        <Button
          type="submit"
          data-testid="invite-teammate-submit-btn"
          disabled={loading}
          className="self-end bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm h-10"
        >
          {loading ? (<><Spinner size={14} className="mr-2 animate-spin" /> Sending…</>) : (<><EnvelopeSimple size={14} className="mr-2" /> Send invite</>)}
        </Button>
      </form>

      <div className="flex items-center justify-between border-t border-zinc-800/60 pt-4 mb-2">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Recent invitations</div>
        <button
          data-testid="invite-teammate-refresh-btn"
          onClick={load}
          disabled={loadingList}
          className="text-xs text-zinc-500 hover:text-orange-400 flex items-center gap-1"
        >
          <ArrowClockwise size={12} className={loadingList ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {listError ? (
        <div data-testid="invite-list-error" className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-sm px-3 py-2">
          {listError}
        </div>
      ) : invites.length === 0 ? (
        <div data-testid="invite-empty" className="text-sm text-zinc-500 py-4 text-center border border-zinc-800/60 rounded-sm bg-zinc-950/40">
          No invitations sent yet.
        </div>
      ) : (
        <div className="divide-y divide-zinc-800/60 border border-zinc-800/60 rounded-sm bg-zinc-950/40">
          {invites.slice(0, 10).map((inv) => {
            const s = STATE_STYLES[inv.state] || STATE_STYLES.pending;
            const Icon = s.icon;
            const canResend = inv.state !== "used"; // pending or expired
            return (
              <div key={inv.id} data-testid={`invite-row-${inv.email}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{inv.name}</div>
                  <div className="text-xs text-zinc-500 truncate font-mono">{inv.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${s.bg} ${s.color}`}>
                    <Icon size={10} weight="fill" /> {inv.state}
                  </span>
                  {canResend && (
                    <button
                      type="button"
                      data-testid={`invite-resend-${inv.email}`}
                      onClick={() => resend(inv.id)}
                      disabled={resending === inv.id}
                      title="Send a fresh invitation link (invalidates any prior link)"
                      className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/50 transition-colors inline-flex items-center gap-1"
                    >
                      {resending === inv.id ? (<><Spinner size={10} className="animate-spin" /> Sending</>) : (<><PaperPlaneTilt size={10} weight="fill" /> Resend</>)}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
